import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  GlobalStyles,
  Grid,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ConstatService from "../../../services/smq/ConstatService";
import SuiviService from "../../../services/smq/SuiviService";
import { getDisplayedNcStatus } from "../NC/ncStatusHelper";

const EMPTY_VALUE = "—";

const CRITICITE_COLORS = {
  critique: { backgroundColor: "#fde2e2", color: "#b42318" },
  majeure: { backgroundColor: "#fff3cd", color: "#9a5b00" },
  mineure: { backgroundColor: "#d8f8e6", color: "#107844" },
};

const STATUT_COLORS = {
  delai_depasse: { backgroundColor: "#ffe1e1", color: "#d32f2f" },
  done: { backgroundColor: "#d8f8e6", color: "#107844" },
  done_after_reopen: { backgroundColor: "#d8f8e6", color: "#107844" },
  in_progress: { backgroundColor: "#fff2bf", color: "#9a5b00" },
  open: { backgroundColor: "#e1efff", color: "#175b9c" },
  reopen: { backgroundColor: "#fde2e2", color: "#b42318" },
};

const STAGE_COLORS = {
  1: { background: "#ddf7fa", color: "#009daf" },
  2: { background: "#e8f2ff", color: "#2476d2" },
  3: { background: "#fff2df", color: "#e87519" },
  4: { background: "#f1e9ff", color: "#7330df" },
  5: { background: "#e8f8ef", color: "#188d4e" },
  6: { background: "#fdeaea", color: "#d92d27" },
  7: { background: "#eef3f8", color: "#36546f" },
};

const displayValue = (value) => String(value ?? "").trim() || EMPTY_VALUE;

const formatDate = (value) => {
  if (!value) return EMPTY_VALUE;
  const dateValue = String(value).slice(0, 10);
  const date = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("fr-FR");
};

const formatDuration = (start, end) => {
  if (!start || !end) return EMPTY_VALUE;
  const startDate = new Date(`${String(start).slice(0, 10)}T00:00:00`);
  const endDate = new Date(`${String(end).slice(0, 10)}T00:00:00`);
  const duration = Math.round((endDate - startDate) / 86400000);
  return Number.isFinite(duration) && duration >= 0 ? `${duration}j` : EMPTY_VALUE;
};

const statusLabel = (value) => {
  if (value === "delai_depasse") return "Délai dépassé";
  if (value === "done") return "Clôturée";
  if (value === "done_after_reopen") return "Clôturée après réouverture";
  if (value === "in_progress") return "En cours";
  if (value === "open") return "Ouverte";
  if (value === "reopen") return "NC réouverte — action insuffisante";
  return displayValue(value);
};

const curatifStatusLabel = (value) => {
  if (value === "a_faire") return "À faire";
  if (value === "en_cours") return "En cours";
  if (value === "realisee") return "Réalisée";
  return displayValue(value);
};

const actionStatusLabel = (value) => {
  if (value === "confirmed") return "Confirmée";
  if (value === "pending") return "En attente";
  return displayValue(value);
};

const suiviDecisionLabel = (value) => {
  if (value === "EFFICACE") return "Efficacité confirmée";
  if (value === "INEFFICACE") return "Action insuffisante — NC réouverte";
  if (value === "A_REVERIFIER") return "En attente de vérification";
  return displayValue(value);
};

const fiveMLabel = (value) => {
  const labels = {
    main_oeuvre: "Main-d'œuvre",
    materiel: "Matériel",
    matiere: "Matière",
    methode: "Méthode",
    milieu: "Milieu",
  };
  const values = String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  if (!values.length) return EMPTY_VALUE;
  return values.map((item) => labels[item] || item).join(", ");
};

const DetailField = ({ label, value, width = 6 }) => (
  <Grid item md={width} xs={12}>
    <Typography
      color="#5a7093"
      fontSize={9}
      fontWeight={800}
      letterSpacing="0.04em"
      textTransform="uppercase"
    >
      {label}
    </Typography>
    <Typography color="#10263d" fontSize={13} mt={0.35} whiteSpace="pre-wrap">
      {displayValue(value)}
    </Typography>
  </Grid>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.number,
};

const DetailStage = ({ children, number, title }) => {
  const colors = STAGE_COLORS[number];
  return (
    <Box
      sx={{
        border: `1px solid ${colors.color}33`,
        borderRadius: "10px",
        mb: 1.5,
        overflow: "hidden",
        pageBreakInside: "avoid",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: colors.background,
          color: colors.color,
          display: "flex",
          fontSize: 11,
          fontWeight: 800,
          gap: 1,
          px: 1.5,
          py: 0.9,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: colors.color,
            borderRadius: "50%",
            color: "#fff",
            display: "flex",
            height: 23,
            justifyContent: "center",
            width: 23,
          }}
        >
          {number}
        </Box>
        {title}
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  );
};

DetailStage.propTypes = {
  children: PropTypes.node.isRequired,
  number: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]).isRequired,
  title: PropTypes.string.isRequired,
};

const FamilyHistory = ({ detail, excludedConstatId }) => {
  const children = Array.isArray(detail.sous_ncs) ? detail.sous_ncs : [];
  const relatedChildren = children
    .filter(
      (child) => String(child.constat_id) !== String(excludedConstatId)
    )
    .sort((left, right) => {
      const reopenDifference = Number(left.reopen_ref || 0) - Number(right.reopen_ref || 0);
      if (reopenDifference) return reopenDifference;
      return String(left.constat_date || "").localeCompare(String(right.constat_date || ""));
    });
  const rows = [
    {
      constat_date: detail.constat_date,
      constat_id: detail.constat_id,
      criticite: detail.criticite,
      delai: detail.deadline,
      description_constat: detail.description_constat,
      has_reopened_child: detail.has_reopened_child,
      reference: detail.reference,
      relation: "Origine",
      reopen_ref: null,
      statut: detail.status,
    },
    ...relatedChildren.map((child) => ({ ...child, relation: "Sous-NC" })),
  ];

  return (
    <DetailStage number={7} title="Historique de filiation">
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ "& th": { backgroundColor: "#173d61", color: "#fff", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" } }}>
              <TableCell>Relation</TableCell>
              <TableCell>Référence</TableCell>
              <TableCell>Réouverture</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Criticité</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rowStatus = row.statut === "done" && row.has_reopened_child
                ? "done_after_reopen"
                : getDisplayedNcStatus(row.statut, row.delai);
              const rowCriticite = String(row.criticite || "").toLowerCase();

              return (
                <TableRow key={`${row.relation}-${row.constat_id}`} sx={{ "& td": { borderColor: "#d5e0eb", fontSize: 11, verticalAlign: "top" } }}>
                  <TableCell>
                    <Box component="span" sx={{ backgroundColor: row.relation === "Origine" ? "#e1efff" : "#f1e9ff", borderRadius: "14px", color: row.relation === "Origine" ? "#175b9c" : "#7330df", fontSize: 10, fontWeight: 800, px: 1, py: 0.45 }}>
                      {row.relation}
                    </Box>
                  </TableCell>
                  <TableCell><Typography color="#10263d" fontSize={11} fontWeight={800}>{displayValue(row.reference)}</Typography></TableCell>
                  <TableCell>{row.reopen_ref ? `R${String(row.reopen_ref).padStart(2, "0")}` : EMPTY_VALUE}</TableCell>
                  <TableCell>{formatDate(row.constat_date)}</TableCell>
                  <TableCell>
                    <Box component="span" sx={{ ...(CRITICITE_COLORS[rowCriticite] || { backgroundColor: "#edf2f7", color: "#52677c" }), borderRadius: "14px", fontSize: 10, fontWeight: 800, px: 1, py: 0.45 }}>
                      {displayValue(row.criticite)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{ ...(STATUT_COLORS[rowStatus] || { backgroundColor: "#edf2f7", color: "#52677c" }), borderRadius: "14px", fontSize: 10, fontWeight: 800, px: 1, py: 0.45 }}>
                      {statusLabel(rowStatus)}
                    </Box>
                  </TableCell>
                  <TableCell>{displayValue(row.description_constat)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </DetailStage>
  );
};

FamilyHistory.propTypes = {
  detail: PropTypes.object.isRequired,
  excludedConstatId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const NCDetailDialog = ({ onClose, tabs = [], target }) => {
  const navigate = useNavigate();
  const hasTabs = tabs.length > 0;
  const open = Boolean(target) || hasTabs;
  const [activeTab, setActiveTab] = useState(0);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestRef = useRef(0);
  const detailCacheRef = useRef({});
  const activeTabConfig = hasTabs ? tabs[activeTab] || null : null;

  const dialogKey = useMemo(
    () =>
      hasTabs
        ? tabs.map((tab) => tab.target?.constat_id || "").join(":")
        : target?.constat_id || "",
    [hasTabs, tabs, target?.constat_id]
  );

  useEffect(() => {
    requestRef.current += 1;
    detailCacheRef.current = {};
    setActiveTab(0);
    setDetail(null);
    setError(null);
    setLoading(false);
  }, [dialogKey]);

  useEffect(() => {
    setCurrentTarget(
      hasTabs ? tabs[activeTab]?.target || null : target || null
    );
  }, [activeTab, hasTabs, tabs, target]);

  useEffect(() => {
    if (!currentTarget?.constat_id) {
      requestRef.current += 1;
      setDetail(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const cacheKey = String(currentTarget.constat_id);
    const cachedResult = detailCacheRef.current[cacheKey];
    if (cachedResult) {
      setDetail(cachedResult.detail || null);
      setError(cachedResult.error || null);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    setDetail(null);
    setError(null);
    setLoading(true);

    Promise.all([
      SuiviService.getDetail(currentTarget.constat_id),
      ConstatService.getConstat(currentTarget.constat_id),
    ])
      .then(([suiviResponse, constatResponse]) => {
        if (requestId !== requestRef.current) return;
        if (!suiviResponse?.data?.constat_id) {
          throw new Error("Le service a retourné un détail de NC invalide.");
        }
        const constatDetail = constatResponse?.data || {};
        const loadedDetail = {
          ...suiviResponse.data,
          analyse: {
            ...(suiviResponse.data.analyse || {}),
            pourquois: constatDetail.analyse_pourquois || [],
          },
          actions_correctives:
            constatDetail.actions_correctives ||
            suiviResponse.data.actions_correctives ||
            [],
          sous_ncs: constatDetail.sous_ncs || [],
        };
        detailCacheRef.current[cacheKey] = {
          detail: loadedDetail,
          error: null,
        };
        setDetail(loadedDetail);
      })
      .catch((requestError) => {
        if (requestId !== requestRef.current) return;
        const message =
          requestError?.message || "Impossible de charger le détail de la NC.";
        detailCacheRef.current[cacheKey] = { detail: null, error: message };
        setError(message);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });

    return () => {
      if (requestId === requestRef.current) requestRef.current += 1;
    };
  }, [currentTarget, reloadToken]);

  const latestSuivi = useMemo(() => {
    const suivis = detail?.suivis || [];
    return [...suivis].sort((left, right) => {
      const dateDifference =
        new Date(right.date_verification || 0).getTime() -
        new Date(left.date_verification || 0).getTime();

      return dateDifference || Number(right.suivi_id || 0) - Number(left.suivi_id || 0);
    })[0] || null;
  }, [detail]);
  const displayedStatus = useMemo(() => {
    if (!detail) return null;
    if (detail.status === "done" && detail.has_reopened_child) {
      return "done_after_reopen";
    }
    return getDisplayedNcStatus(detail.status, detail.deadline);
  }, [detail]);
  const criticite = String(detail?.criticite || "").toLowerCase();
  const openSubDetail = (subNc) => {
    if (hasTabs) {
      const tabIndex = tabs.findIndex(
        (tab) => String(tab.target?.constat_id) === String(subNc.constat_id)
      );
      if (tabIndex >= 0) {
        setActiveTab(tabIndex);
        return;
      }
    }
    setCurrentTarget(subNc);
  };
  const treatSubNc = (subNc) => {
    navigate(`/smq_nc/add?constat_id=${subNc.constat_id}&step=constat`);
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "@media print": {
            "@page": { margin: "12mm", size: "auto" },
            "body *": { visibility: "hidden !important" },
            "#nc-detail-print, #nc-detail-print *": {
              visibility: "visible !important",
            },
            "#nc-detail-print": {
              boxShadow: "none !important",
              left: "0 !important",
              margin: "0 !important",
              maxHeight: "none !important",
              maxWidth: "none !important",
              overflow: "visible !important",
              position: "absolute !important",
              top: "0 !important",
              width: "100% !important",
            },
            "#nc-detail-print .MuiDialogContent-root": {
              maxHeight: "none !important",
              overflow: "visible !important",
            },
            ".nc-detail-print-hide": { display: "none !important" },
          },
        }}
      />
      <Dialog
        aria-labelledby="nc-detail-title"
        fullWidth
        maxWidth="lg"
        onClose={onClose}
        open={open}
        PaperProps={{
          id: "nc-detail-print",
          sx: {
            borderRadius: { xs: 0, sm: "14px" },
            maxHeight: { xs: "100%", sm: "92vh" },
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            backgroundColor: "#12243f",
            color: "#fff",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            px: { xs: 2, sm: 3 },
            py: 2.25,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              fontSize={{ xs: 19, sm: 23 }}
              fontWeight={900}
              id="nc-detail-title"
              noWrap
            >
              {displayValue(detail?.reference || currentTarget?.reference)}
            </Typography>
            <Typography color="#a9b7c8" fontSize={13} mt={0.35}>
              {displayValue(detail?.processus || currentTarget?.processus)} ·{" "}
              {formatDate(detail?.constat_date || currentTarget?.constat_date)} ·{" "}
              {displayValue(detail?.direction_code)}
            </Typography>
          </Box>
          <Box
            className="nc-detail-print-hide"
            sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              disabled={!detail || loading}
              onClick={() => window.print()}
              startIcon={<PrintOutlinedIcon />}
              sx={{
                borderColor: "#66758a",
                color: "#fff",
                flex: { xs: 1, sm: "none" },
                textTransform: "none",
                "&:hover": { borderColor: "#fff" },
              }}
              variant="outlined"
            >
              Imprimer
            </Button>
            <Button
              onClick={onClose}
              startIcon={<CloseOutlinedIcon />}
              sx={{
                borderColor: "#a52d3b",
                color: "#ff7b83",
                flex: { xs: 1, sm: "none" },
                textTransform: "none",
                "&:hover": { borderColor: "#ff7b83" },
              }}
              variant="outlined"
            >
              Fermer
            </Button>
          </Box>
        </Box>

        {hasTabs && (
          <Tabs
            aria-label="Filiation de la non-conformité"
            className="nc-detail-print-hide"
            onChange={(event, value) => {
              setReloadToken(0);
              setActiveTab(value);
            }}
            sx={{
              borderBottom: "1px solid #d5e0eb",
              minHeight: 44,
              px: { xs: 1, sm: 3 },
              "& .MuiTab-root": {
                fontSize: 12,
                fontWeight: 800,
                minHeight: 44,
                textTransform: "none",
              },
              "& .Mui-selected": { color: "#173d61" },
              "& .MuiTabs-indicator": { backgroundColor: "#173d61" },
            }}
            value={activeTab}
          >
            {tabs.map((tab) => (
              <Tab key={`${tab.label}-${tab.target?.constat_id}`} label={tab.label} />
            ))}
          </Tabs>
        )}

        <DialogContent sx={{ backgroundColor: "#fff", overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
          {loading && (
            <Box aria-label="Chargement du détail" role="status">
              <Skeleton height={38} width="35%" />
              {[1, 2, 3, 4].map((item) => (
                <Skeleton height={125} key={item} sx={{ mb: 1 }} variant="rounded" />
              ))}
            </Box>
          )}
          {error && !loading && (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => {
                    if (currentTarget?.constat_id) {
                      delete detailCacheRef.current[String(currentTarget.constat_id)];
                    }
                    setReloadToken((value) => value + 1);
                  }}
                  size="small"
                >
                  Réessayer
                </Button>
              }
              severity="error"
            >
              {error}
            </Alert>
          )}
          {detail && !loading && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Box sx={{ ...STATUT_COLORS[displayedStatus], borderRadius: "16px", fontSize: 11, fontWeight: 800, px: 1.5, py: 0.65 }}>
                  {statusLabel(displayedStatus)}
                </Box>
                <Box sx={{ ...(CRITICITE_COLORS[criticite] || { backgroundColor: "#edf2f7", color: "#52677c" }), borderRadius: "16px", fontSize: 11, fontWeight: 800, px: 1.5, py: 0.65 }}>
                  {displayValue(detail.criticite)}
                </Box>
              </Box>

              <DetailStage number={1} title="Constat d'écart">
                <Grid container spacing={1.5}>
                  <DetailField label="Référence NC" value={detail.reference} />
                  <DetailField label="Date du constat" value={formatDate(detail.constat_date)} />
                  <DetailField label="Type de déclencheur" value={[detail.trigger_code, detail.trigger_name].filter(Boolean).join(" — ")} />
                  <DetailField label="Constaté par" value={detail.constat_user_name} />
                  <DetailField label="Direction / Service" value={[detail.direction_code, detail.direction_name].filter(Boolean).join(" — ")} />
                  <DetailField label="Processus concerné" value={detail.processus} />
                  <DetailField label="Description du constat" value={detail.description_constat} width={12} />
                </Grid>
              </DetailStage>

              <DetailStage number={2} title="Ouverture de la Fiche NC">
                <Grid container spacing={1.5}>
                  <DetailField label="Type d'exigence" value={[detail.exigence_code, detail.exigence_name].filter(Boolean).join(" — ")} />
                  <DetailField label="Pilote responsable" value={detail.pilote_name} />
                  <DetailField label="Exigence précise non respectée" value={detail.description_exigence} width={12} />
                  <DetailField label="Gravité" value={detail.gravite} />
                  <DetailField label="Fréquence" value={detail.frequence} />
                  <DetailField label="Criticité" value={detail.criticite} />
                  <DetailField label="Délai de traitement" value={formatDate(detail.deadline)} />
                </Grid>
              </DetailStage>

              <DetailStage number={3} title="Action curative immédiate">
                <Grid container spacing={1.5}>
                  <DetailField label="Description de l'action curative" value={detail.curatif?.description} width={12} />
                  <DetailField label="Responsable" value={detail.curatif?.responsable_name} />
                  <DetailField label="Date de réalisation" value={formatDate(detail.curatif?.date_realisation)} />
                  <DetailField label="Statut" value={curatifStatusLabel(detail.curatif?.statut)} />
                </Grid>
              </DetailStage>

              <DetailStage number={4} title="Analyse des causes">
                <Grid container spacing={1.5}>
                  <DetailField label="Problème de départ" value={detail.analyse?.probleme_depart} width={12} />
                  <Grid item xs={12}>
                    <Typography color="#5a7093" fontSize={9} fontWeight={800} letterSpacing="0.04em" textTransform="uppercase">
                      Pourquoi
                    </Typography>
                    {(detail.analyse?.pourquois || []).length ? (
                      <Box component="ol" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                        {detail.analyse.pourquois.map((item) => (
                          <Box component="li" key={`${item.ordre}-${item.texte}`} sx={{ color: "#10263d", fontSize: 12, mb: 0.5 }}>
                            {displayValue(item.texte)} ({fiveMLabel(item.famille_5m)})
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="#10263d" fontSize={13} mt={0.35}>{EMPTY_VALUE}</Typography>
                    )}
                  </Grid>
                  <DetailField label="Cause racine identifiée" value={detail.analyse?.cause_racine} width={12} />
                </Grid>
              </DetailStage>

              <DetailStage number={5} title="Actions correctives">
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 1180 }}>
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#173d61", color: "#fff", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" } }}>
                        <TableCell>Action corrective</TableCell><TableCell>Plan d'action</TableCell><TableCell>Indicateur</TableCell><TableCell>Avancement</TableCell><TableCell>Début</TableCell><TableCell>Fin</TableCell><TableCell>Délai</TableCell><TableCell>Responsable</TableCell><TableCell>Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail.actions_correctives || []).length ? detail.actions_correctives.map((action) => (
                        <TableRow key={action.plan_action_id} sx={{ "& td": { borderColor: "#d5e0eb", fontSize: 11, verticalAlign: "top" } }}>
                          <TableCell>{displayValue(action.action_corrective)}</TableCell><TableCell>{displayValue(action.plan_action)}</TableCell><TableCell>{displayValue(action.indicateur_efficacite)}</TableCell><TableCell>{`${action.avancement ?? 0}%`}</TableCell><TableCell>{formatDate(action.date_debut)}</TableCell><TableCell>{formatDate(action.date_fin)}</TableCell><TableCell>{formatDuration(action.date_debut, action.date_fin)}</TableCell><TableCell>{displayValue(action.responsable_name)}</TableCell><TableCell>{actionStatusLabel(action.status)}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow><TableCell align="center" colSpan={9}>Aucune action corrective renseignée</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DetailStage>

              <DetailStage number={6} title="Suivi de l'efficacité">
                <Grid container spacing={1.5}>
                  <DetailField label="Date de vérification" value={formatDate(latestSuivi?.date_verification)} />
                  <DetailField label="Vérifié par" value={latestSuivi?.evaluateur_name} />
                  <DetailField label="Indicateur d'efficacité" value={latestSuivi?.indicateur_efficacite} width={12} />
                  <DetailField label="Valeur de l'indicateur" value={latestSuivi?.valeur_indicateur} />
                  <DetailField label="Décision" value={suiviDecisionLabel(latestSuivi?.decision)} />
                  <DetailField label="Résultat de la vérification" value={latestSuivi?.resultat_verification} width={12} />
                  <DetailField label="Commentaire" value={latestSuivi?.commentaire} width={12} />
                </Grid>
              </DetailStage>

              {activeTabConfig?.showFamilyHistory ? (
                <FamilyHistory
                  detail={detail}
                  excludedConstatId={activeTabConfig.excludedConstatId}
                />
              ) : (detail.sous_ncs || []).length > 0 && (
                <DetailStage number={7} title="Sous-NC réouvertes">
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 980 }}>
                      <TableHead>
                        <TableRow sx={{ "& th": { backgroundColor: "#173d61", color: "#fff", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" } }}>
                          <TableCell>Référence</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Criticité</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.sous_ncs.map((subNc) => {
                          const subStatus =
                            subNc.statut === "done" && subNc.has_reopened_child
                              ? "done_after_reopen"
                              : getDisplayedNcStatus(subNc.statut, subNc.delai);
                          const subCriticite = String(subNc.criticite || "").toLowerCase();
                          return (
                            <TableRow key={subNc.constat_id} sx={{ "& td": { borderColor: "#d5e0eb", fontSize: 11, verticalAlign: "top" } }}>
                              <TableCell>
                                <Typography color="#10263d" fontSize={11} fontWeight={800}>
                                  {displayValue(subNc.reference)}
                                </Typography>
                                <Typography color="#60758a" fontSize={10}>
                                  Réouverture {displayValue(subNc.reopen_ref)}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDate(subNc.constat_date)}</TableCell>
                              <TableCell>
                                <Box component="span" sx={{ ...(CRITICITE_COLORS[subCriticite] || { backgroundColor: "#edf2f7", color: "#52677c" }), borderRadius: "14px", fontSize: 10, fontWeight: 800, px: 1, py: 0.45 }}>
                                  {displayValue(subNc.criticite)}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box component="span" sx={{ ...(STATUT_COLORS[subStatus] || { backgroundColor: "#edf2f7", color: "#52677c" }), borderRadius: "14px", fontSize: 10, fontWeight: 800, px: 1, py: 0.45 }}>
                                  {statusLabel(subStatus)}
                                </Box>
                              </TableCell>
                              <TableCell>{displayValue(subNc.description_constat)}</TableCell>
                              <TableCell align="center">
                                <Box className="nc-detail-print-hide" sx={{ display: "inline-flex", gap: 0.75 }}>
                                  <Button
                                    onClick={() => openSubDetail(subNc)}
                                    size="small"
                                    startIcon={<VisibilityOutlinedIcon />}
                                    sx={{ fontSize: 11, textTransform: "none" }}
                                    variant="outlined"
                                  >
                                    Voir
                                  </Button>
                                  <Button
                                    onClick={() => treatSubNc(subNc)}
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    sx={{ fontSize: 11, textTransform: "none" }}
                                    variant="contained"
                                  >
                                    Traiter
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DetailStage>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

NCDetailDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      excludedConstatId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string.isRequired,
      showFamilyHistory: PropTypes.bool,
      target: PropTypes.shape({
        constat_date: PropTypes.string,
        constat_id: PropTypes.number.isRequired,
        processus: PropTypes.string,
        reference: PropTypes.string,
      }).isRequired,
    })
  ),
  target: PropTypes.shape({
    constat_date: PropTypes.string,
    constat_id: PropTypes.number.isRequired,
    processus: PropTypes.string,
    reference: PropTypes.string,
  }),
};

export default NCDetailDialog;
